import {
  ApiFilter,
  ApiFilterType,
  ApiGroupedMultipleSelectionFilter,
  ApiListOptions,
  ApiMultipleSelectionFilter,
  ApiNumberRangeFilter,
  ApiSingleSelectionFilter,
  ApiPagination,
  ApiSortDirection,
  ApiSorting,
  ApiSortOption
} from '@ibe/api';
import { action, computed, observable, makeObservable } from 'mobx';
import { PaginationProps } from '../../Components/CustomPagination/CustomPagination';
import { FilterProperties, SortingProperties } from './externals';

class ServiceListStore {
  _defaultPagination: ApiPagination = {
    startingFrom: 0,
    showing: 30,
    currentPage: 1,
    ofTotal: 0
  };

  filterOpen = false;

  _defaultFilter: ApiFilter[] = [];

  _defaultSorting: ApiSorting = {
    sortDirection: ApiSortDirection.ASC,
    sortOption: ApiSortOption.PRICE
  };

  _defaultListOption: ApiListOptions = {
    filter: this._defaultFilter,
    pagination: this._defaultPagination,
    sorting: this._defaultSorting,
    filteredCount: 0,
    count: 0
  };

  _listOptions: ApiListOptions = this._defaultListOption;

  _filter: ApiFilter[] = this._defaultFilter;

  _sorting: ApiSorting = this._defaultSorting;

  _pagination: ApiPagination = this._defaultPagination;

  _hidePagination = false;

  _hideSorter = false;

  _customFilter?: (item: unknown) => boolean;

  constructor() {
    makeObservable(this, {
      _listOptions: observable,
      _filter: observable,
      _customFilter: observable,
      _sorting: observable,
      _pagination: observable,
      _hidePagination: observable,
      _hideSorter: observable,

      filterOpen: observable,

      listOptions: computed,
      filter: computed,
      pagination: computed,
      sorting: computed,

      listCountFiltered: computed,
      listCountFull: computed,

      setListOptions: action,
      setFilter: action,
      setFilters: action,
      setFilterOpen: action,
      clearFilters: action,
      setCustomFilter: action,
      clearCustomFilter: action,

      setSorting: action,
      setPaginationShowing: action,
      setPaginationStart: action,

      hidePagination: action,
      hideSorter: action
    });
  }

  get listOptions(): ApiListOptions {
    return this._listOptions;
  }

  get filter(): ApiFilter[] {
    return this._filter;
  }

  get pagination(): ApiPagination {
    return this._pagination;
  }

  get sorting(): ApiSorting {
    return this._sorting;
  }

  get listCountFull(): number | undefined {
    return this._listOptions.count;
  }

  get isCustomFilter(): boolean {
    return !!this._customFilter;
  }

  get listCountFiltered(): number | undefined {
    return this._listOptions.filteredCount;
  }

  setListOptions(listOptions: ApiListOptions): void {
    this._listOptions = listOptions;
    if (listOptions.filter) {
      this._filter = listOptions.filter;
    }
    if (listOptions.pagination) {
      this._pagination = listOptions.pagination;
    }
    if (listOptions.sorting) {
      this._sorting = listOptions.sorting;
    }
  }

  clearFilters(): void {
    this._listOptions.filter = this._defaultFilter;
  }

  clearCustomFilter(): void {
    this._customFilter = undefined;
  }

  setFilterOpen(open: boolean): void {
    this.filterOpen = open;
  }

  setFilter(filter: ApiFilter): void {
    // replace single filter
    const index = this._filter.findIndex(f => f.category === filter.category);
    if (index > -1) {
      this._filter.splice(index, 1);
    }
    this._filter.splice(index, 0, filter);

    this._pagination.startingFrom = 0;
    if (this._pagination.showing === 0) {
      this._pagination.showing = this._defaultPagination.showing;
    }
  }

  setFilters(filters: ApiFilter[]): void {
    this._filter = filters;
  }

  setCustomFilter(customFilter: (item: unknown) => boolean): void {
    this._customFilter = customFilter;
  }

  resetFilters(): void {
    this.filter.forEach(filter => {
      let castedFilter:
        | ApiMultipleSelectionFilter
        | ApiGroupedMultipleSelectionFilter
        | ApiSingleSelectionFilter
        | ApiNumberRangeFilter;
      switch (filter.type) {
        case ApiFilterType.MultipleSelectionFilter:
          castedFilter = filter as ApiMultipleSelectionFilter;
          castedFilter.selected = undefined;
          castedFilter.selectedOptions = undefined;
          this.setFilter(castedFilter);
          break;
        case ApiFilterType.GroupedMultipleSelectionFilter:
          castedFilter = filter as ApiGroupedMultipleSelectionFilter;
          castedFilter.selected = undefined;
          castedFilter.selectedOptions = undefined;
          this.setFilter(castedFilter);
          break;
        case ApiFilterType.SingleSelectionFilter:
          castedFilter = filter as ApiSingleSelectionFilter;
          castedFilter.selected = undefined;
          castedFilter.selectedOption = undefined;
          this.setFilter(castedFilter);
          break;
        case ApiFilterType.NumberRangeFilter:
          castedFilter = filter as ApiNumberRangeFilter;
          castedFilter.minSelected = undefined;
          castedFilter.maxSelected = undefined;
          castedFilter.min = 0;
          castedFilter.max = 0;
          this.setFilter(castedFilter);
          break;
        default:
          break;
      }
    });
  }

  hidePagination(val: boolean): void {
    this._hidePagination = val;
  }

  hideSorter(val: boolean): void {
    this._hideSorter = val;
  }

  setSorting(sorting?: ApiSorting): void {
    this._sorting = sorting || this._defaultSorting;
  }

  setPaginationShowing(showing: number): void {
    this._pagination.showing = showing;
    this._pagination.currentPage = Math.ceil((this._pagination.startingFrom + 1) / showing);
    this._pagination.startingFrom = (this._pagination.currentPage - 1) * showing;
  }

  setPaginationStart(start: number): void {
    this._pagination.startingFrom = start;
  }

  getPaginationProperties(callback: (a?: ApiPagination) => Promise<void>): PaginationProps {
    return {
      amount: this.pagination.showing,
      index: this.pagination.startingFrom,
      currentPage: this.pagination.currentPage,
      totalCount: this.pagination.ofTotal,
      showing: this.pagination.showing,
      onAmountChange: async (amount: number): Promise<void> => {
        if (amount - this.pagination.showing !== 0) {
          this.setPaginationShowing(amount);
          callback(this._pagination);
        }
      },
      onNext: async (): Promise<void> => {
        const p = this.pagination;
        const targetStart = p.startingFrom + p.showing;
        if (targetStart < p.ofTotal) {
          p.currentPage += 1;
          this.setPaginationStart(targetStart);
          callback(this._pagination);
        }
      },
      onPrev: async (): Promise<void> => {
        const p = this.pagination;
        const targetStart = p.startingFrom - p.showing;
        if (targetStart >= 0) {
          p.currentPage -= 1;
          this.setPaginationStart(targetStart);
          callback(this._pagination);
        }
      },
      onFirst: async (): Promise<void> => {
        const p = this.pagination;
        const targetStart = p.startingFrom + p.showing;
        if (targetStart >= 0) {
          p.currentPage = 1;
          this.setPaginationStart(0);
          callback(this._pagination);
        }
      },
      onLast: async (): Promise<void> => {
        const p = this.pagination;
        const targetStart = p.startingFrom + p.showing;
        if (targetStart < p.ofTotal) {
          p.currentPage = Math.ceil((p.ofTotal as number) / p.showing);
          this.setPaginationStart((p.currentPage - 1) * p.showing);
          callback(this._pagination);
        }
      },
      onPageNumber: async (pageNumber: number): Promise<void> => {
        const p = this.pagination;
        const targetStart = (pageNumber - 1) * p.showing;
        if (targetStart < p.ofTotal && targetStart !== p.startingFrom) {
          p.currentPage = pageNumber;
          this.setPaginationStart((p.currentPage - 1) * p.showing);
          callback(this._pagination);
        }
      },
      hidden: this._hidePagination
    };
  }

  getFilterProperties = (
    callback: () => Promise<void>,
    singleSelectCategories?: boolean
  ): FilterProperties => {
    return {
      singleSelectCategories,
      filter: this.filter,
      onFilterChange: async (
        filter:
          | ApiMultipleSelectionFilter
          | ApiNumberRangeFilter
          | ApiGroupedMultipleSelectionFilter
          | ApiSingleSelectionFilter
      ): Promise<void> => {
        this.setFilter(filter);
        callback();
      },
      state: {
        isOpen: this.filterOpen,
        onChange: (value: boolean): void => {
          this.setFilterOpen(value);
        }
      },
      onFiltersDiscard: async (): Promise<void> => {
        this.resetFilters();
        callback();
      }
    };
  };

  getSortProps = (
    callback: () => Promise<void>,
    sortingOptions: ApiSortOption[],
    sortingType?: SortingProperties['sortingType']
  ): SortingProperties => {
    return {
      onChange: async (sorting: ApiSorting | undefined): Promise<void> => {
        this.setSorting(sorting);
        callback();
      },
      sorting: this._sorting,
      sortingOptions,
      sortingType,
      hidden: this._hideSorter
    };
  };
}

export default ServiceListStore;
